import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import { SceneController } from "../../engine/viewer/SceneController";

const ORIGIN_STYLE = {
  spec: "text-[var(--c-green)]",
  rule: "text-[var(--c-accent)]",
  template: "text-[var(--c-amber)]",
  assumed: "text-[var(--c-amber)]",
  user: "text-[var(--c-text)]",
};

/** §7 viewer core: one Design Model in, an interactive stage out. */
const DesignViewer = forwardRef(function DesignViewer({ model, onSelect, className = "" }, ref) {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const labelRef = useRef(null);
  const ctrlRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [ui, setUi] = useState({ labels: true, dims: false, xray: false, view: model?.views?.[0]?.id });
  const [sel, setSel] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!model || !stageRef.current) return undefined;
    let ctrl;
    try {
      ctrl = new SceneController({
        canvas: canvasRef.current,
        stage: stageRef.current,
        labelLayer: labelRef.current,
        model,
        onSelect: (id) => { setSel(id); onSelect?.(id); },
        onState: (s) => setUi((u) => ({ ...u, ...s })),
        onStats: setStats,
      });
      ctrlRef.current = ctrl;
      setFailed(false);
    } catch (err) {
      console.error("[viewer3d]", err);
      setFailed(true);
    }
    return () => { ctrl?.dispose(); ctrlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useImperativeHandle(ref, () => ({
    select: (id) => ctrlRef.current?.select(id, true),
    goTo: (v) => ctrlRef.current?.goTo(v),
    controller: () => ctrlRef.current,
  }));

  const part = sel ? (model?.parts || []).find((p) => p.id === sel) : null;
  const views = model?.views || [];
  const legend = model?.xray?.legend || [];

  return (
    <div ref={stageRef} data-testid="v3d-stage"
      className={`relative min-h-0 overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--v3d-stage)] ${className}`}>
      <canvas ref={canvasRef} data-testid="v3d-canvas" tabIndex={0}
        className="block h-full w-full outline-none" style={{ touchAction: "none" }}
        aria-label="Interactive 3D model. Drag to orbit, scroll to zoom." />
      <div ref={labelRef} className="v3d-labels" />

      <div className="pointer-events-none absolute left-2.5 right-2.5 top-2.5 flex flex-wrap justify-between gap-2">
        <div className="pointer-events-auto flex flex-wrap gap-1 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] p-[3px]" role="group" aria-label="Camera view">
          {views.map((v) => (
            <button key={v.id} type="button" data-testid={`v3d-view-${v.id}`}
              aria-pressed={ui.view === v.id}
              onClick={() => ctrlRef.current?.goTo(v.id)}
              className={`rounded px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors duration-150 ${
                ui.view === v.id ? "bg-[var(--c-accent)] text-white" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="pointer-events-auto flex flex-wrap gap-1 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] p-[3px]" role="group" aria-label="Display">
          {[["labels", "Labels", (v) => ctrlRef.current?.setLabels(v)],
            ["dims", "Dimensions", (v) => ctrlRef.current?.setDims(v)],
            ["xray", "Active part", (v) => ctrlRef.current?.setXray(v)]].map(([key, label, fn]) => (
            <label key={key} className={`flex cursor-pointer items-center gap-1.5 rounded px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider ${
              ui[key] ? "text-[var(--c-text)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              <input type="checkbox" data-testid={`v3d-toggle-${key}`} className="h-3.5 w-3.5 accent-[var(--c-accent)]"
                checked={!!ui[key]} onChange={(e) => fn(e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {ui.xray && legend.length > 0 && (
        <div data-testid="v3d-xray-legend"
          className="absolute left-2.5 top-[3.4rem] flex flex-wrap gap-2.5 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-1.5 font-mono text-[10px]">
          {legend.map((l) => (
            <span key={l.label} className="inline-flex items-center gap-1.5 text-[var(--c-text)]">
              <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: model.materials?.[l.mat]?.color }} />
              {l.label}
            </span>
          ))}
        </div>
      )}

      {part && (
        <div data-testid="v3d-part-card"
          className="absolute bottom-2.5 left-2.5 w-[min(380px,calc(100%-20px))] rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-3.5 shadow-xl">
          <button type="button" data-testid="v3d-part-close" aria-label="Close"
            onClick={() => ctrlRef.current?.select(null)}
            className="absolute right-1.5 top-1.5 rounded p-1 text-[var(--c-muted)] hover:bg-[var(--c-bg)] hover:text-[var(--c-text)]">
            <X size={14} />
          </button>
          <div className="font-mono text-[10px] font-bold tracking-wider text-[var(--c-accent)]">
            {part.clause}{part.quantity ? ` · ${part.quantity}` : ""}
          </div>
          <h3 className="mb-1.5 mr-7 mt-0.5 text-base font-semibold text-[var(--c-text)]">{part.name}</h3>
          <p className="max-h-32 overflow-y-auto text-[12.5px] leading-relaxed text-[var(--c-text)]">{part.spec}</p>
          {!!part.sources?.length && (
            <div className="mt-2.5 border-t border-[var(--c-border)] pt-2" data-testid="v3d-part-sources">
              <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">Value sources</div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-[10.5px]">
                {part.sources.map((s) => (
                  <div key={s.field + s.value} className="col-span-2 grid grid-cols-[auto_1fr_auto] items-baseline gap-x-2">
                    <dt className="text-[var(--c-muted)]">{s.field}</dt>
                    <dd className="truncate text-[var(--c-text)]" title={s.value}>{s.value}</dd>
                    <dd className={ORIGIN_STYLE[s.origin] || "text-[var(--c-muted)]"} title={s.ref || s.origin}>
                      {s.origin}{s.ref ? ` · ${s.ref}` : ""}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}

      <p className="pointer-events-none absolute bottom-2.5 right-3 m-0 text-right font-mono text-[10px] text-[var(--c-muted)]"
        data-testid="v3d-badge">
        {model?.meta?.badge}
        {stats ? ` · ${stats.drawCalls} draw calls · ${(stats.triangles / 1000).toFixed(0)}k tris` : ""}
      </p>
      {!part && (
        <p className="pointer-events-none absolute bottom-7 right-3 m-0 hidden text-right text-[11px] text-[var(--c-muted)] sm:block">
          Drag to orbit · scroll to zoom · right-drag to pan · click a part
        </p>
      )}

      {failed && (
        <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-[var(--c-muted)]" data-testid="v3d-webgl-fail">
          <p>The 3D view needs WebGL, which this browser didn&apos;t provide. The component list still shows every part of the Design Model.</p>
        </div>
      )}
    </div>
  );
});

export default DesignViewer;
